<template>
  <div class="w-full h-full relative flex flex-col overflow-auto bg-primary-50">
    <div class="mobile-status-padding"></div>
    <!-- Load tracking on mount and poll -->
    <VBusHelper
      :emitOnMounted="{
        event: $constants.EVENTS.ORDERS.TRACK,
        payload: { id: orderId }
      }"
    />
    <VPollOn
      :event="$constants.EVENTS.ORDERS.TRACK"
      :payload="{ id: orderId }"
      :interval="10000"
    />

    <!-- Back Button -->
    <button
      @click="goBack"
      class="fixed top-4 left-4 z-10 w-10 h-10 rounded-full bg-white shadow flex items-center justify-center"
      :style="{ top: 'calc(16px + env(safe-area-inset-top, 0px))' }"
    >
      <svg
        class="w-5 h-5 text-slate-700"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M15 19l-7-7 7-7"
        />
      </svg>
    </button>

    <!-- Map Section -->
    <div class="h-[100%] flex-shrink-0 overflow-hidden sticky top-0">
      <Map ref="mapRef" @ready="traceRouteWithDelay" />
    </div>

    <!-- Content Area (Overlapping Map) -->
    <div class="flex-1 px-4 pb-8 -mt-[35vh] relative z-20">
      <!-- Loading / Error States -->
      <div
        v-if="$stores.orders.state.tracking.loading && !tracking"
        class="liquidGlassCard p-6 rounded-3xl mb-4 text-center"
      >
        <div
          class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3"
        ></div>
        <p class="text-slate-600 font-medium">Chargement du suivi...</p>
      </div>

      <div
        v-else-if="$stores.orders.state.tracking.error"
        class="liquidGlassCard p-6 rounded-3xl mb-4 text-center"
      >
        <p class="text-red-500 font-medium mb-3">
          {{ $stores.orders.state.tracking.error }}
        </p>
        <button
          @click="retry"
          class="px-6 py-2 bg-primary text-white rounded-xl font-bold"
        >
          Réessayer
        </button>
      </div>

      <!-- Main Tracking Content -->
      <template v-else-if="tracking">
        <!-- Header / Status Card -->
        <div
          class="liquidGlassCard p-6 rounded-3xl mb-4 overflow-hidden relative"
        >
          <div
            class="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-16 -mt-16 blur-3xl"
          ></div>

          <div class="flex justify-between items-start mb-6 relative">
            <div>
              <div class="text-lg font-bold title-color">
                Suivi de la livraison
              </div>
              <div
                class="text-xs font-bold mt-1 px-3 py-1 rounded-full inline-block"
                :class="getStatusClass(tracking.status)"
              >
                {{ getStatusLabel(tracking.status) }}
              </div>
            </div>
            <div class="text-right">
              <div class="text-sm font-bold title-color">
                {{ formatPrice(tracking.totalPrice || tracking.total) }}
              </div>
              <div class="text-xs opacity-70 title-color">
                Commande #{{ tracking.id || tracking.orderNumber || 'N/A' }}
              </div>
            </div>
          </div>

          <!-- ETA Section (if delivering) -->
          <div
            v-if="etaMessage && isStepActive('picked_up')"
            class="mt-4 p-4 bg-primary/10 rounded-2xl border-2 border-primary border-dashed animate-pulse"
          >
            <div
              class="text-primary font-bold text-center uppercase tracking-wider mb-1 text-sm"
            >
              {{ etaMessage.title }}
            </div>
            <div class="text-primary-600 text-xs text-center font-medium">
              {{ etaMessage.subtitle }}
            </div>
          </div>

          <div class="border-t border-slate-100 pt-6 mt-6">
            <div class="flex items-center gap-3 mb-4">
              <div
                class="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"
              >
                <svg
                  class="w-4 h-4 text-slate-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <div
                class="text-sm font-bold text-slate-800 uppercase tracking-wider"
              >
                Détails de la livraison
              </div>
            </div>
            <div class="space-y-3">
              <div class="flex gap-3">
                <div
                  class="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0"
                ></div>
                <div class="text-sm">
                  <span
                    class="text-slate-500 block text-xs font-bold uppercase tracking-tight mb-0.5"
                    >De</span
                  >
                  <span class="text-slate-800 font-medium">{{
                    tracking.pickupLabel || tracking.storeName || 'Restaurant'
                  }}</span>
                </div>
              </div>
              <div class="flex gap-3">
                <div
                  class="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 shrink-0"
                ></div>
                <div class="text-sm">
                  <span
                    class="text-slate-500 block text-xs font-bold uppercase tracking-tight mb-0.5"
                    >À</span
                  >
                  <span class="text-slate-800 font-medium">{{
                    tracking.destinationLabel ||
                    tracking.destinationAddress ||
                    tracking.deliveryAddress?.street ||
                    'Votre adresse'
                  }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Live Location Indicator -->
          <div
            v-if="tracking.riderLat && tracking.riderLng"
            class="border-t border-slate-100 pt-4 mt-6 flex items-center justify-between"
          >
            <div class="flex items-center gap-2 text-green-600">
              <div
                class="w-2.5 h-2.5 bg-green-500 rounded-full animate-ping mr-1"
              ></div>
              <span class="text-xs font-bold uppercase tracking-wider"
                >Position en direct</span
              >
            </div>
            <span class="text-xs text-slate-400">
              Mise à jour à l'instant
            </span>
          </div>
        </div>

        <!-- Rider Card -->
        <div v-if="tracking.rider" class="liquidGlassCard p-6 rounded-3xl mb-4">
          <div class="text-lg font-bold mb-4 title-color">Votre livreur</div>
          <div class="flex items-center gap-4">
            <ImgComponent
              :src="tracking.rider.photo || '/images/avatar-placeholder.png'"
              :alt="tracking.rider.name"
              class="w-16 h-16 rounded-full object-cover border-2 border-primary/20"
            />
            <div class="flex-1">
              <div class="font-bold text-slate-800 text-lg">
                {{ tracking.rider.name }}
              </div>
              <div
                class="flex items-center gap-1 text-sm text-yellow-500 font-medium"
              >
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path
                    d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                  />
                </svg>
                {{ tracking.rider.rating || 'Nouveau' }}
              </div>
            </div>
          </div>
          <div class="flex gap-3 mt-4">
            <a
              :href="`tel:${tracking.rider.phone}`"
              class="flex-1 py-2.5 bg-primary text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-primary-600 transition-colors"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              Appeler
            </a>
            <button
              class="flex-1 py-2.5 bg-slate-100 text-slate-700 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-slate-200 transition-colors"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              Message
            </button>
          </div>
        </div>

        <!-- Timeline -->
        <div
          class="liquidGlassCard flex flex-col items-center rounded-3xl p-4 mb-4"
        >
          <OrderStep
            icon="/images/icons/money.svg"
            title="Paiement confirmé"
            description="Votre commande a été payée avec succès"
            :active="getStepStatus('payment')"
            :date="formatDate(tracking.stepsTimeline?.payment)"
            step-name="payment"
            :all-steps="allSteps"
            :timeline="tracking.stepsTimeline"
          />
          <OrderStep
            icon="/images/icons/cook.svg"
            title="Cuisine débutée"
            description="Votre repas est en cours de préparation"
            :active="getStepStatus('preparing')"
            :date="formatDate(tracking.stepsTimeline?.preparing)"
            step-name="preparing"
            :all-steps="allSteps"
            :timeline="tracking.stepsTimeline"
          />
          <OrderStep
            icon="/images/icons/dish.svg"
            title="Repas cuisiné"
            description="Votre commande est prête pour la livraison"
            :active="getStepStatus('cooked')"
            :date="formatDate(tracking.stepsTimeline?.cooked)"
            step-name="cooked"
            :all-steps="allSteps"
            :timeline="tracking.stepsTimeline"
          />
          <OrderStep
            icon="/images/icons/scooter.svg"
            title="Livraison en cours"
            description="Votre commande est en chemin"
            :active="getStepStatus('deliveryStarted')"
            :date="formatDate(tracking.stepsTimeline?.deliveryStarted)"
            step-name="deliveryStarted"
            :all-steps="allSteps"
            :timeline="tracking.stepsTimeline"
          />
          <OrderStep
            icon="/images/icons/home.svg"
            title="Commande livrée"
            description="Livrée à votre porte. Bon appétit !"
            :active="getStepStatus('delivered')"
            :date="formatDate(tracking.stepsTimeline?.delivered)"
            step-name="delivered"
            :all-steps="allSteps"
            :timeline="tracking.stepsTimeline"
          />
        </div>

        <!-- Delivery Code (if available and not delivered) -->
        <div
          v-if="tracking.deliveryCode && !isStepActive('delivered')"
          class="liquidGlassCard p-6 rounded-3xl mb-4 text-center"
        >
          <div
            class="text-sm font-semibold mb-2 text-slate-500 uppercase tracking-wider"
          >
            Code de confirmation
          </div>
          <div
            class="text-3xl font-black text-primary flex justify-center gap-2 items-center cursor-pointer mb-2"
            @click="copyDeliveryCode"
          >
            <span>{{ tracking.deliveryCode }}</span>
            <svg
              class="w-6 h-6 text-slate-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </div>
          <div class="text-xs text-red-500 font-medium max-w-[200px] mx-auto">
            À ne donner au livreur qu'une fois la commande reçue
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import Map from '@features/map/Map.vue';
import OrderStep from '../components/OrderStep.vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';
import Commons from '@features/shared/libraries/commons';

const route = useRoute();
const orderId = ref(route.params.id);
const mapRef = ref(null);
const onceLaunched = ref(false);

const tracking = computed(() => window.$stores?.orders?.state?.tracking);

// Watch for tracking data updates to re-trace route if needed
watch(
  () => tracking.value,
  (newVal) => {
    if (newVal && onceLaunched.value) {
      updateRiderPosition();
    }
  },
  { deep: true }
);

onMounted(() => {
  // Try to use existing order data initially if available from list
  const existingOrder = window.$stores?.orders?.state?.list?.items?.find(
    (o) => o.id === orderId.value
  );
  if (existingOrder && !tracking.value) {
    // We could pre-populate but let the store handle it via the event
  }
});

// ETA Message Calculation
const etaMessage = computed(() => {
  const durationRaw =
    tracking.value?.durationRemaining ||
    tracking.value?.delivery?.durationRemaining;
  let duration = null;

  if (typeof durationRaw === 'number') {
    duration = durationRaw;
  } else if (typeof durationRaw === 'string') {
    const match = durationRaw.match(/(\d+)/);
    if (match) duration = parseInt(match[1], 10);
  }

  if (duration === null) return null;

  if (duration <= 2) {
    return {
      title: 'LE LIVREUR EST LÀ',
      subtitle: 'Sortez pour réceptionner votre commande'
    };
  } else {
    return {
      title: `ARRIVÉE ESTIMÉE DANS ${duration} MIN`,
      subtitle: 'Préparez-vous à recevoir votre commande'
    };
  }
});

// Timeline steps logic
const allSteps = [
  'payment',
  'preparing',
  'cooked',
  'deliveryStarted',
  'delivered'
];

function getStepStatus(step) {
  const timeline = tracking.value?.stepsTimeline || {};
  const status = tracking.value?.status || '';

  const currentStepIndex = getStatusStepIndex(status);

  switch (step) {
    case 'payment':
      return !!timeline.payment || currentStepIndex >= 0;
    case 'preparing':
      return !!timeline.preparing || currentStepIndex >= 1;
    case 'cooked':
      return !!timeline.cooked || currentStepIndex >= 2;
    case 'deliveryStarted':
      return !!timeline.deliveryStarted || currentStepIndex >= 3;
    case 'delivered':
      return !!timeline.delivered || status === 'delivered';
    default:
      return false;
  }
}

function getStatusStepIndex(status) {
  const statusMap = {
    pending: 0,
    confirmed: 0,
    preparing: 1,
    ready: 2,
    ready_for_pickup: 2,
    picked_up: 3,
    in_transit: 3,
    delivered: 4,
    cancelled: -1
  };
  return statusMap[status] ?? -1;
}

const isStepActive = (stepStatus) => {
  return (
    tracking.value?.status === stepStatus ||
    (stepStatus === 'picked_up' && tracking.value?.status === 'in_transit')
  );
};

const getStatusLabel = (status) => {
  const labels = {
    pending: 'En attente',
    confirmed: 'Confirmée',
    preparing: 'En préparation',
    ready: 'Prête',
    ready_for_pickup: 'Prête',
    picked_up: 'En livraison',
    in_transit: 'En livraison',
    delivered: 'Livrée',
    cancelled: 'Annulée'
  };
  return labels[status] || status;
};

const getStatusClass = (status) => {
  const classes = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    preparing: 'bg-indigo-100 text-indigo-800',
    ready: 'bg-emerald-100 text-emerald-800',
    ready_for_pickup: 'bg-emerald-100 text-emerald-800',
    picked_up: 'bg-primary/20 text-primary-700',
    in_transit: 'bg-primary/20 text-primary-700',
    delivered: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800'
  };
  return classes[status] || 'bg-gray-100 text-gray-800';
};

// Map Integration
function traceRouteWithDelay() {
  setTimeout(traceRoute, 500); // Give tracking data time to load
}

function traceRoute() {
  if (!mapRef.value || !tracking.value) {
    // If tracking data isn't ready yet, retry shortly
    if (!onceLaunched.value) {
      setTimeout(traceRoute, 1000);
    }
    return;
  }

  onceLaunched.value = true;

  // Fallback coordinates if backend doesn't provide them initially
  const pickupLat =
    parseFloat(tracking.value.pickupLat || tracking.value.restaurant?.lat) ||
    6.3571;
  const pickupLng =
    parseFloat(tracking.value.pickupLng || tracking.value.restaurant?.lng) ||
    2.3699;

  const destLatRaw =
    tracking.value.destinationLat ||
    tracking.value.deliveryAddress?.coordinates?.lat ||
    tracking.value.deliveryAddress?.lat;
  const destLngRaw =
    tracking.value.destinationLng ||
    tracking.value.deliveryAddress?.coordinates?.lng ||
    tracking.value.deliveryAddress?.lng;
  const destinationLat = parseFloat(destLatRaw) || 6.3832;
  const destinationLng = parseFloat(destLngRaw) || 2.4156;

  const checkpoints = (tracking.value.checkpoints || [])
    .map((cp, i) => ({
      lat: parseFloat(cp.lat || cp.coordinates?.lat),
      lng: parseFloat(cp.lng || cp.coordinates?.lng),
      content: `Point de passage ${i + 1}`,
      iconType: 'checkpoint'
    }))
    .filter((cp) => !isNaN(cp.lat) && !isNaN(cp.lng));

  mapRef.value.traceRouteWithPoints(
    `order-route-${orderId.value}`,
    {
      lat: pickupLat,
      lng: pickupLng,
      content: tracking.value.storeName || 'Restaurant',
      iconType: 'restaurant'
    },
    {
      lat: destinationLat,
      lng: destinationLng,
      content: 'Destination',
      iconType: 'destination'
    },
    checkpoints
  );

  updateRiderPosition();
}

function updateRiderPosition() {
  if (!tracking.value?.riderLat || !tracking.value?.riderLng || !mapRef.value)
    return;
  mapRef.value.setMarker(
    {
      position: {
        lat: parseFloat(tracking.value.riderLat),
        lng: parseFloat(tracking.value.riderLng)
      },
      content: 'Votre Livreur',
      iconType: 'rider'
    },
    `order-route-${orderId.value}`,
    'rider-position'
  );
}

// Formatters
function formatPrice(amount) {
  if (!amount && amount !== 0) return '0 FCFA';
  return (
    new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0
    }).format(amount) + ' FCFA'
  );
}

function formatDate(timestamp) {
  if (!timestamp) return '';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(timestamp));
}

function copyDeliveryCode() {
  if (!tracking.value?.deliveryCode) return;
  Commons.copyToClipboard(
    tracking.value.deliveryCode.split('-')[1],
    () => {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        type: 'success',
        title: 'Copié',
        body: 'Code copié dans le presse-papier'
      });
    },
    (err) => {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        type: 'error',
        title: 'Erreur de copie',
        body: 'Impossible de copier le code'
      });
      console.log(err);
    }
  );
}

const retry = () => {
  bus.emit(constants.EVENTS.ORDERS.TRACK, {
    id: orderId.value
  });
};

const goBack = () => {
  window.$router?.back();
};
</script>

<style scoped>
/* Scoped styles are minimized as we use Tailwind classes corresponding to V3 design system */
</style>
